import React, { useState, useEffect, MutableRefObject, useCallback,useRef } from 'react'
import { DataGridRef } from 'devextreme-react/data-grid'
import { TreeListRef } from 'devextreme-react/tree-list'
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import TextBox, { TextBoxRef } from 'devextreme-react/text-box'
import Button from 'devextreme-react/button'
import List from 'devextreme-react/list'
import { Popup, Position, ToolbarItem } from 'devextreme-react/popup'
import { useTranslation } from 'react-i18next'
import { BaseIcons } from 'enums/base-icons'
import { SettingType } from 'helpers/setting-type-enum'
import { useIxAuth } from 'contexts/auth-context'
import { IxConfirmPopup } from 'components'

import client from 'api'

import './layout-chooser.scss'

const setLayoutsData = (settingName: any, value: any, id: any, userId: any, settingKey: string, setId: any) => {
    if (id) {
        client.put(`/api/ixweb/ixusersettings/${id}/`, {
            "SETTINGDATA_1": value
            })
    }
    else {
        client.post('/api/ixweb/ixusersettings/', {"SETTINGDATA_1": value, "SETTINGKEY": settingKey, "SETTINGTYPE": SettingType.ModuleSetting, "SETTINGNR": 0, "SETTINGNAME": settingName, "USERID": userId})
            .then((res: any) => {
                setId(res.data[0].HDR_ID)
            })
    }
}

type LayoutItemType = {
    name: string,
    state: any
}


type ILayoutChooserContentProps = {
    datagrid: MutableRefObject<DataGridRef | TreeListRef | null>,
    gridName: string,
    onHiding?: () => void,
    moduleName?: string,
}

// exported for testing
export const LayoutChooserContent = ({
    gridName,
    datagrid,
    onHiding,
    moduleName='',
}: ILayoutChooserContentProps) => {
    const [name, setName] = useState('')
    const [error, setError] = useState(false)
    const [existLayoutError, setExistLayoutError] = useState(false)
    const [layouts, _setLayouts] = useState<LayoutItemType[]>([])
    const [nameTouched, setNameTouched] = useState(false)
    const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)
    const [selectedLayoutName, setSelectedLayoutName] = useState(null)
    const { t } = useTranslation()
    const tbRef = useRef<TextBoxRef>(null)
    const { user } = useIxAuth()
    const [selectedLayout, setSelectedLayout] = useState<any>(null)
    const [id, setId] = useState(null)

    const setLayouts = useCallback((lastLayout: string | null, layouts: LayoutItemType[]) => {
        const layoutsData = { last: lastLayout, layouts }
        setLayoutsData(`grid-layouts-${gridName}`, JSON.stringify(layoutsData), id, user?.id, moduleName, setId)
        _setLayouts(layouts)

    }, [gridName, user?.id, moduleName, id])

    const handleNameChange = useCallback((e: any) => {
        setName(e.value)
        e.event && setNameTouched(true)

        if (!e.value || e.value === '') {
            setError(true)
            setExistLayoutError(false)
        } else {
            setError(false)
            const existLayout = layouts.find((l) => l.name === e.value)
            if (existLayout) {
                setExistLayoutError(true)
            } else {
                setExistLayoutError(false)
            }
        }
    }, [layouts])

    const LayoutItem = ({ data }: any) => {
        return (
            <div
                className={`layout-list--item ${data.name === selectedLayout && 'layout-list--item-selected'}`}
                data-name={data.name}
                onClick={loadLayout}
                >
                <div className='layout-list--item--icon'>
                    <i className='dx-icon-rowfield' />
                </div>
                <div className='layout-list--item--name'>
                    {data.name}
                </div>
                {
                    selectedLayout === data.name &&
                    <div className='layout-list--item--icon layout-list--item--btn-save'>
                        <i className='dx-icon-save' title={t('global:button.save')} onClick={saveLayoutChanges}/>
                    </div>

                }
                <div className='layout-list--item--icon layout-list--item--btn-delete'>
                    <i className='dx-icon-trash' title={t('global:button.delete')} data-name={data.name} onClick={deleteLayout}/>
                </div>
            </div>
        )
    }

    const saveLayout = useCallback((_: any) => {
        if (!name || name === '') {
            setError(true)
            setNameTouched(true)
            return
        }

        if (!datagrid || !datagrid.current) {
            return
        }

        if (existLayoutError) {
            setNameTouched(true)
            return
        }

        const currentState = datagrid.current.instance().state()
        setLayouts(name, [...layouts, { name, state: { ...currentState, selectedRowKeys: [] } }])
        setSelectedLayout(name)

        tbRef.current?.instance().reset()
        setNameTouched(false)
    }, [datagrid, existLayoutError, layouts, name, setLayouts])

    const saveLayoutChanges = useCallback((e: any) => {
        e.stopPropagation()


        if (!datagrid || !datagrid.current) {
            return
        }

        const layout = layouts.find((i) => i.name === selectedLayout)
        const layoutState = layout && layout.state
        const currentState = { ...datagrid.current.instance().state(), selectedRowKeys: [] }

        if(JSON.stringify(layoutState) !== JSON.stringify(currentState)) {
            setLayouts(selectedLayout, layouts.map((layout) => {
                if(layout.name === selectedLayout) {
                    layout.state = currentState;
                }
                return layout
            }))
        }

    },[selectedLayout, layouts, datagrid, setLayouts])

    const loadLayout = useCallback((e: any) => {
        const layout = layouts.find((l) => l.name === e.currentTarget.dataset.name)
        const state = layout && layout.state
        onHiding && onHiding()
        setSelectedLayout(e.currentTarget.dataset.name)
        setLayouts(e.currentTarget.dataset.name, layouts)

        if (datagrid && datagrid.current && state) {
            const filter = datagrid.current.instance().filter()
            datagrid.current.instance().state(state)
            datagrid.current.instance().filter(filter)
        }
    }, [datagrid, onHiding, layouts, setLayouts])

    const restoreDefaultLayout = useCallback((__: any) => {
        onHiding && onHiding()
        setSelectedLayout(null)
        setLayouts(null, layouts)

        if (datagrid && datagrid.current) {
            const filter = datagrid.current.instance().filter()
            datagrid.current.instance().state({})
            datagrid.current.instance().filter(filter)
        }
    }, [datagrid, onHiding, layouts, setLayouts])

    const deleteLayout = useCallback((e: any) => {
        e.stopPropagation()
        setConfirmPopupVisible(true)
        setSelectedLayoutName(e.currentTarget.dataset.name)
    }, [])

    const confirmDeleteLayout = useCallback(() => {
        const filtredLayoutList = layouts.filter((i) => i.name !== selectedLayoutName)
        setLayouts(selectedLayoutName === selectedLayout ? null : selectedLayout, filtredLayoutList)
        setConfirmPopupVisible(false)
    }, [layouts, selectedLayoutName, setLayouts, selectedLayout])

    const rejectDeleteLayout = useCallback(() => {
        setSelectedLayoutName(null)
        setConfirmPopupVisible(false)
    }, [])

    useEffect(() => {
        // load any saved layouts on start up
        client.get(`/api/ixweb/ixusersettings/?action=eql&SETTINGKEY=${moduleName}&SETTINGTYPE=${SettingType.ModuleSetting}&SETTINGNR=0&SETTINGNAME=grid-layouts-${gridName}&USERID=${user?.id}`).then((res: any) => {
            const data = res.data[0]
            data && setId(data.HDR_ID)

            const layoutsData = JSON.parse(data?.SETTINGDATA_1 || '[]')

            if (Array.isArray(layoutsData)) {
                _setLayouts(layoutsData)
            } else {
                const { layouts, last } = layoutsData
                const lastLayout = layouts.find((l: any) => l.name === last)
                if (last && lastLayout) {
                    setSelectedLayout(last)
                    if (datagrid && datagrid.current && lastLayout.state) {
                        // get dataSource filter and reapply it after changing the state so it's not lost
                        const filter = datagrid.current.instance().filter()
                        datagrid.current.instance().state(lastLayout.state)
                        datagrid.current.instance().filter(filter)
                    }
                }
                _setLayouts(layouts)
            }
        })

    }, [gridName, user?.id, datagrid, moduleName])

    return (
        <ResponsiveBox className='layout-chooser'>
            <Row />
            <Row />
            <Row />

            <Col />

            <Item>
                <Location row={0} col={0} />

                <div className='layout-chooser--content'>
                    <List
                        dataSource={layouts}
                        itemComponent={LayoutItem}
                        className='layout-list '
                        noDataText='No saved layouts'
                        scrollByContent={true}
                        height={layouts.length > 5 ? 200 : ''}
                    />
                </div>

                <IxConfirmPopup
                        popupVisible={confirmPopupVisible}
                        title={t('global:label.warning')}
                        description={t('global:question.deleteLayout', { layoutName: selectedLayoutName})}
                        onConfirm={confirmDeleteLayout}
                        onHiding={rejectDeleteLayout}
                    />
            </Item>

            <Item>
                <Location row={2} col={0} />
                <div className='layout-chooser--content'>
                    <div>
                        <label>
                            {t('global:field.layoutName')}
                            <TextBox
                                ref={tbRef}
                                onValueChanged={handleNameChange}
                                isValid={error ? (!error || !nameTouched) : (!nameTouched || !existLayoutError)}
                                valueChangeEvent='keyup'
                                placeholder={t('global:field.layoutName')}
                            />
                            { error && nameTouched ? <small className='layout-name-error'>Name is required</small> : null }
                            { existLayoutError && nameTouched ? <small className='layout-name-error'>Layout already exist</small> : null }
                        </label>
                    </div>
                    <div style={ { display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' } }>
                        <Button icon={ BaseIcons.New } text={t('global:button.createLayout')} onClick={saveLayout} className='layout-chooser--save-btn' />
                        <Button icon='rowfield' text={t('global:button.defaultLayout')} onClick={restoreDefaultLayout} className='layout-chooser--save-btn' />
                    </div>
                </div>
            </Item>
        </ResponsiveBox>
    )
}


type ILayoutChooserProps = {
    datagrid: MutableRefObject<DataGridRef | TreeListRef | null>,
    gridName: string,
    visible: boolean,
    onHiding?: () => void,
    moduleName?: string,
}

export const LayoutChooser = ({ datagrid, gridName, visible, onHiding, moduleName }: ILayoutChooserProps) => {
    const { t } = useTranslation()

    const responsiveBox = useCallback(() => {
        return (
            <LayoutChooserContent
                moduleName={moduleName}
                gridName={gridName}
                datagrid={datagrid}
                onHiding={onHiding}
            />
        )
    }, [datagrid, gridName, onHiding, moduleName])

    return (
        <Popup
            visible={visible}
            dragEnabled={false}
            hideOnOutsideClick={false}
            contentRender={responsiveBox}
            showCloseButton={false}
            shading={false}
            showTitle={true}
            title={t('global:label.layoutsTitle')}
            width={350}
            height='auto'
        >
            <Position
                at="right top"
                my="right top"
                offset="0 50"
                of={CSS.escape(`#${gridName}-grid`)}
            />

            <ToolbarItem
                widget="dxButton"
                location="after"
                options={{
                    icon: 'remove',
                    onClick: () => onHiding && onHiding()
                }}
            />
        </Popup>
    )
}
